import React from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useBreakpoint } from 'hooks';
import { AdminContext, AuthContext } from 'contexts';
import { Page, IconNameType, message, Tile, Modal, Spin } from 'components';
import { UserRoles } from '@bb/users';
import { UserController } from 'api';
import {
  checkUserBillingPermission,
  featureEnabled,
  hasValidStripeMlsProductId,
  makeModalResponsive,
} from 'utils';
import { AnsweringSubscriptionJsonType } from '@bb/domain/dist/types';
import { findActiveSubscription } from '../../../api/AnsweringSubscriptionController';

type ButtonItemType = {
  icon: IconNameType;
  lines: [string, string];
  to?: string;
  href?: string;
  badgeCount?: number;
  onClick?: () => void;
};

const AdminPage: React.FC = () => {
  const { md } = useBreakpoint();
  const history = useHistory();
  const {
    auth,
    hasFeature,
    answeringPortalConfig,
    loadingAnsweringPortalConfig,
  } = React.useContext(AuthContext);
  const { ongoingTransferCount } = React.useContext(AdminContext);
  const [loadingInitial, setLoadingInitial] = React.useState(true);
  const [analyticsLink, setAnalyticsLink] = React.useState('');
  const [accountOwnerString, setAccountOwnerString] = React.useState('');
  const [activeAnsweringSubscription, setActiveAnsweringSubscription] =
    React.useState<AnsweringSubscriptionJsonType>();

  const billingTileOnClick = React.useCallback(() => {
    const showErrorModal = (content: string) => {
      makeModalResponsive(
        Modal.error({
          title: 'You are not authorized to manage billing',
          content,
          centered: true,
        }),
      );
    };

    if (auth.assumingUserId || auth.isAssumedNotBB) {
      showErrorModal('You cannot manage billing while assuming a user.');
    } else if (!checkUserBillingPermission(auth)) {
      showErrorModal(
        `You must ask the account owner${
          accountOwnerString ? ` ${accountOwnerString} ` : ' '
        }for permission.`,
      );
    } else {
      history.push('/billing');
    }
  }, [accountOwnerString, history, auth]);

  const answeringTileOnClick = React.useCallback(() => {
    history.push('/admin/appointment_desk');
  }, [history]);

  const canEditListing = featureEnabled('supraHideAdminFunctionalityFeature')
    ? (hasFeature('SUPRA_MVP') &&
        (auth.org.country === 'CAN' || auth.parentOrgType === 'mlsStaff')) ||
      !hasFeature('SUPRA_MVP')
    : true;

  const buttonList: ButtonItemType[] = (
    [
      {
        icon: 'antdFolderOutlined',
        lines: ['Draft', 'Listings'],
        to: '/listing/list/admin/0',
      },
      {
        icon: 'delete',
        lines: ['Deleted', 'Listings'],
        to: '/listing/list/deleted/0',
      },

      ...(canEditListing
        ? [
            {
              icon: 'plus',
              lines: ['Create', 'Listing'],
              to: '/listing/new',
            },
          ]
        : []),
      {
        icon: 'antdUserOutlined',
        lines: ['Manage', 'Users'],
        to: '/users/list',
        badgeCount: ongoingTransferCount,
      },

      ...(canEditListing
        ? [
            {
              icon: 'antdShrinkOutlined',
              lines: ['Merge / Combine', 'Listings'],
              to: '/listing/merge/options',
            },
          ]
        : []),
      {
        icon: 'bullhorn',
        lines: ['Send', 'Announcement'],
        to: '/announcement/list',
      },
      auth.roles[auth.role] >= auth.roles.BRANCH_ADMIN &&
        hasFeature('ANALYTICS') && {
          icon: 'chart',
          lines: ['Reporting', 'Analytics'],
          href: analyticsLink || '',
        },
      auth.roles[auth.role] >= auth.roles.GLOBAL_ADMIN && {
        icon: 'antdPhoneOutlined',
        lines: ['Live Concierge', 'Service'],
        onClick: answeringTileOnClick,
      },
      {
        icon: 'lock',
        lines: ['Manage', 'Lock Boxes'],
        to: '/lockboxes',
      },
      {
        icon: 'template',
        lines: ['Template', 'Manager'],
        to: '/template/manage',
      },
      {
        icon: 'settings',
        lines: ['Brokerage', 'Settings'],
        to: '/org/settings',
      },
      auth.roles[auth.role] >= auth.roles.BRANCH_ADMIN && {
        icon: 'antdCreditCardOutlined',
        lines: ['Brokerage', 'Billing & Invoices'],
        onClick: billingTileOnClick,
      },
    ] as ButtonItemType[]
  ).filter(Boolean);

  const getAnalyticsUrl = async () => {
    try {
      const {
        data: { analyticsUrl },
      } = await axios.get<{ analyticsUrl: string }>('/secrets/analyticsUrl');

      setAnalyticsLink(analyticsUrl || '');
    } catch (err) {
      message.error('Server error. Please try again later');
      history.replace('/');
    }
  };

  const getOrgOwnerString = async () => {
    try {
      const { users } = await UserController.org({ role: [UserRoles.Owner] });

      const owner = users.find(({ role }) => role === UserRoles.Owner);
      const { firstName, lastName, email } = owner ?? {};

      setAccountOwnerString(
        owner ? `(${firstName} ${lastName}, ${email})` : '',
      );
    } catch (err) {
      message.error('Server error. Please try again later');
      history.replace('/');
    }
  };

  React.useEffect(() => {
    const getAnsweringSubscription = async () => {
      if (
        (answeringPortalConfig?.useLiveAppointmentDesk ||
          hasValidStripeMlsProductId(answeringPortalConfig?.mlsProductId)) &&
        (auth.assumingUserId || auth.isAssumedNotBB || auth.assumerIdMlsStaff)
      ) {
        try {
          const subscriptionData = await findActiveSubscription({
            subscriptionLevel: 'org',
          });
          setActiveAnsweringSubscription(subscriptionData);
        } catch (e) {
          message.error('Error fetching active subscription');
        }
      }
    };

    const fetchInitialData = async () => {
      await Promise.all([
        getAnalyticsUrl(),
        getOrgOwnerString(),
        getAnsweringSubscription(),
      ]);
      setLoadingInitial(false);
    };

    fetchInitialData();
  }, []);

  return (
    <Page type="hollow" title="Admin">
      {loadingAnsweringPortalConfig || loadingInitial ? (
        <StyledSpinContainer>
          <Spin size="large" />
        </StyledSpinContainer>
      ) : (
        <StyledGrid $isMobile={!md}>
          {buttonList.map(({ icon, lines, to, href, badgeCount, onClick }) => {
            const key = lines.join('-');
            return (
              <Tile
                key={key}
                icon={icon}
                lines={lines}
                to={to}
                href={href}
                badgeCount={badgeCount}
                dataCy={`admin-${key}`}
                {...(onClick ? { onClick, to: undefined } : {})}
              />
            );
          })}
        </StyledGrid>
      )}
    </Page>
  );
};

const StyledSpinContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding-top: 50px;
`;

const StyledGrid = styled.div<{ $isMobile: boolean }>`
  display: grid;
  gap: 35px;
  grid-auto-rows: 112px;
  grid-template-columns: repeat(auto-fit, 235px);
  ${({ $isMobile }) =>
    $isMobile &&
    `
  grid-template-columns: 1fr;
  `}
`;

export default AdminPage;
